import React, { useState } from 'react';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import DSBox from '../../../design-system/DSBox';
import DSButton from '../../../design-system/DSButton';
import CollectionReviewDialog from './CollectionReviewDialog';
import { DSTypography } from '../../../design-system/Typography';
import { useTheme } from '@mui/material';
import { DRAFT, PENDING, APPROVED, REJECTED } from '../../../consts/colors';
import { ReactComponent as DraftIcon } from '../../../design-system/Icons/DraftIcon.svg';
import { ReactComponent as MarketplaceIcon } from '../../../design-system/Icons/marketplace.svg';
import { ReactComponent as MarketplacePendingIcon } from '../../../design-system/Icons/marketplacePending.svg';
import {
  CollectionReviewStates,
  MarketplaceInfo,
  MarketplaceStatus,
} from '../../../api/collections';

type CollectionStatusProps = {
  state: CollectionReviewStates;
  marketplace?: MarketplaceInfo;
  /** Edit mode only: given all three, a "Review message" button joins the status. */
  reviewMessage?: string;
  comment?: string;
  onCommentChange?: (comment: string) => void;
};

enum LocalStatus {
  MarketplaceApproved = 'MarketplaceApproved',
}

type StatusType = CollectionReviewStates | LocalStatus;

const statusConfig: Record<
  StatusType,
  { icon: React.ReactElement; color: string }
> = {
  [CollectionReviewStates.Approved]: {
    icon: <CheckCircleOutlineIcon />,
    color: APPROVED,
  },
  [CollectionReviewStates.Pending]: {
    icon: <MarketplacePendingIcon />,
    color: PENDING,
  },
  [CollectionReviewStates.Rejected]: {
    icon: <HighlightOffIcon />,
    color: REJECTED,
  },
  [CollectionReviewStates.Draft]: {
    icon: <DraftIcon />,
    color: DRAFT,
  },
  [LocalStatus.MarketplaceApproved]: {
    icon: <MarketplaceIcon />,
    color: APPROVED,
  },
};

const getCollectionState = (
  state: CollectionReviewStates,
  marketplace?: MarketplaceInfo,
): StatusType => {
  if (marketplace?.status === MarketplaceStatus.True) {
    return LocalStatus.MarketplaceApproved;
  }

  return state;
};

const getStatusIcon = (statusType: StatusType) => {
  const { spacing } = useTheme();
  const config = statusConfig[statusType];

  return React.cloneElement(
    config.icon as React.ReactElement<{ style?: React.CSSProperties }>,
    {
      style: { color: config.color, fontSize: spacing(2.5) },
    },
  );
};

const getStatusText = (statusType: StatusType) => {
  switch (statusType) {
    case LocalStatus.MarketplaceApproved:
      return 'Marketplace';
    default:
      return statusType.charAt(0).toUpperCase() + statusType.slice(1);
  }
};

const CollectionStatus = ({
  state,
  marketplace,
  reviewMessage,
  comment,
  onCommentChange,
}: CollectionStatusProps) => {
  const { spacing, palette, typography } = useTheme();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const statusType = getCollectionState(state, marketplace);

  const badge = (
    <DSBox
      sx={{
        gap: spacing(1),
        display: 'flex',
      }}
    >
      {getStatusIcon(statusType)}
      <DSTypography variant="body1" color={statusConfig[statusType].color}>
        {getStatusText(statusType)}
      </DSTypography>
    </DSBox>
  );

  if (!reviewMessage || comment === undefined || !onCommentChange) {
    return badge;
  }

  return (
    <DSBox sx={{ display: 'flex', alignItems: 'center', gap: spacing(1) }}>
      {badge}
      <DSButton
        variant="text"
        onClick={() => setIsDialogOpen(true)}
        sx={{
          ...typography.body1,
          px: spacing(1),
          gap: spacing(1),
          transition: 'background-color 0.2s ease',
          '&:hover': { backgroundColor: palette.white[8] },
          '&:focus': { backgroundColor: 'transparent' },
        }}
      >
        <ChatBubbleOutlineIcon sx={{ fontSize: spacing(2.5) }} />
        Review message
      </DSButton>
      {isDialogOpen && (
        <CollectionReviewDialog
          reviewMessage={reviewMessage}
          comment={comment}
          onCommentChange={onCommentChange}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </DSBox>
  );
};

export default CollectionStatus;
